import { RotateCcw, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, FIELDS, useBudget, computeBudget, formatMoney } from "@/lib/budget";
import { Reveal, MaskLines, Eyebrow } from "./Reveal";
import { BudgetBar } from "./BudgetBar";
import { PhaseSpend } from "./PhaseSpend";

const MoneyInput = ({ id, label, value, onChange, currency }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A665C]">{label}</span>
    <div className="mt-2 flex items-center rounded-2xl border border-[#C86D51]/20 bg-[#FDFBF7] px-4 transition-colors focus-within:border-[#9E4429]">
      <span className="text-sm text-[#7A665C]">{currency}</span>
      <input type="number" inputMode="decimal" min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`budget-input-${id}`}
        className="h-12 w-full min-w-0 bg-transparent pl-3 font-serif text-2xl text-[#2C221E] outline-none" />
    </div>
  </label>
);

export const Budget = () => {
  const { budget, set, reset } = useBudget();
  const r = computeBudget(budget);
  const cur = budget.currency;
  return (
    <section id="budget" className="relative bg-[#FBF5EE] py-28 sm:py-40" data-testid="budget-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>05 — The cycle-aware budget</Eyebrow></Reveal>
        <div className="mt-6 grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-end">
          <h2 className="font-serif text-5xl font-light leading-[1] tracking-tight text-[#2C221E] sm:text-6xl lg:text-7xl">
            <MaskLines inView delay={0} lines={["Money that moves", <em key="e" className="italic text-[#9E4429]">with your month.</em>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-[#5C4A42] sm:text-lg">Plan bills, savings, food, fun and period care, then split what's left across your four phases, with a little extra room where spending tends to rise.</p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          <Reveal className="min-w-0 self-start rounded-[2rem] border border-[#C86D51]/15 bg-white/60 p-6 sm:p-8 lg:sticky lg:top-28">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A665C]">Currency</span>
              <Select value={cur} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="budget-currency-select" className="h-10 w-[190px] rounded-full border-[#C86D51]/25 bg-[#FDFBF7] text-sm font-semibold text-[#2C221E]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72 rounded-2xl bg-[#FDFBF7]">
                  {CURRENCIES.map(([code, name]) => <SelectItem key={code} value={code} data-testid={`budget-currency-option-${code}`}>{code} · {name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {FIELDS.map(([id, label]) => (
                <div key={id} className={id === "income" ? "sm:col-span-2" : ""}>
                  <MoneyInput id={id} label={label} value={budget[id]} onChange={(v) => set(id, v)} currency={cur} />
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between gap-3 text-xs text-[#6B5A52]">
              <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Saved only in this browser</span>
              <button onClick={reset} data-testid="budget-reset-button" className="flex items-center gap-1.5 font-semibold text-[#9E4429] hover:text-[#843722]">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A665C]">Left for everyday spending</span>
              <span className={`font-serif text-5xl ${r.flexible < 0 ? "text-[#A3271F]" : "text-[#2C221E]"}`} data-testid="budget-flexible-total">{formatMoney(r.flexible, cur)}</span>
            </div>
            {r.flexible < 0 && (
              <p className="mt-3 rounded-2xl bg-[#F9E1DC] p-4 text-sm text-[#7A2318]" data-testid="budget-overspend-warning">
                Your plans add up to more than your income. Try lowering your savings goal or flexible costs for this month.
              </p>
            )}
            <div className="mt-6"><BudgetBar result={r} currency={cur} /></div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {r.phases.map((p) => <PhaseSpend key={p.id} phase={p} currency={cur} careAmount={r.care} />)}
            </div>
            <p className="mt-6 text-xs leading-relaxed text-[#6B5A52]">Based on a 28-day cycle. In the app, Lunelle adjusts to your real cycle length. Budgeting tips are general guidance, not financial advice.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
